import uniq from 'lodash/uniq'

import fields from 'modules/reports/store/collections/fields'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'
import type Report from 'modules/reports/store/models/Report'
import type Template from 'modules/reports/store/models/Template'

interface Props {
  resource: Report | Template
  projections: ProjectionParams[]
}

function hasProjectionWithFunction(props: Props): boolean {
  const { projections } = props

  if (!projections.length) return false

  const firstAggregation = projections.find((p) => {
    return !!p.function
  })

  return !!firstAggregation && !!firstAggregation.path && !!firstAggregation.function
}

function hasProjectionsOnBothUiGroups(props: Props): boolean {
  const fieldsProjections = props.projections.filter((projection) => {
    return !!fields.find({
      path: projection.path,
    })
  })

  const uiGroups = uniq(fieldsProjections.map((projection) => projection.ui_group)).filter(Boolean)

  return uiGroups.length >= 2
}

function canRenderXYAxisChart(props: Props): boolean {
  return props.projections.length > 1 && hasProjectionWithFunction(props)
}

function canRenderTable(props: Props): boolean {
  return props.projections.length > 0
}

function canRenderScatterplot(props: Props): boolean {
  return hasProjectionsOnBothUiGroups(props)
}

const FUNCTIONS: Record<string, (props: Props) => boolean> = {
  bars: canRenderXYAxisChart,
  pie: canRenderXYAxisChart,
  numeric_metric: hasProjectionWithFunction,
  lines: canRenderXYAxisChart,
  table: canRenderTable,
  area: canRenderXYAxisChart,
  scatterplot: canRenderScatterplot,
}

export default function canRender(props: Props): boolean {
  const { resource } = props

  if (!resource.chartSelected) return false

  const checker = FUNCTIONS[resource.type]

  if (!checker) {
    throw Error(`Unknown chart checker: ${resource.type}`)
  }

  return checker(props)
}
